import type { FeatureFlag } from '@recipes/contracts';
import { Alert, Label, Spinner, Switch } from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AdminPage } from '@/features/admin/admin-layout';
import { formatDate } from '@/features/shared/labels';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/feedback/toaster';

export function AdminFlagsPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const flags = useQuery({
    queryKey: queryKeys.admin.flags,
    queryFn: () => api.admin.featureFlags(),
  });
  const update = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.admin.updateFeatureFlag(key, { enabled }),
    onMutate: async ({ key, enabled }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.admin.flags });
      const previous = queryClient.getQueryData<FeatureFlag[]>(queryKeys.admin.flags);
      queryClient.setQueryData<FeatureFlag[]>(queryKeys.admin.flags, (prev) =>
        prev?.map((f) => (f.key === key ? { ...f, enabled } : f)),
      );
      return { previous };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKeys.admin.flags, ctx.previous);
      toastError(e);
    },
    onSuccess: (flag) => {
      toast.success(t('admin.flags.updated', { key: flag.key }));
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.flags });
      void queryClient.invalidateQueries({ queryKey: queryKeys.config });
    },
  });

  return (
    <AdminPage section="flags" description={t('admin.flags.description')}>
      {flags.isLoading && <Spinner label={t('app.loading')} />}
      {flags.error && <Alert variant="destructive">{describeError(flags.error)}</Alert>}
      <ul className="divide-y rounded-lg border">
        {(flags.data ?? []).map((flag) => (
          <li key={flag.key} className="flex items-center gap-4 p-3 md:p-4">
            <div className="min-w-0 flex-1">
              <Label htmlFor={`flag-${flag.key}`} className="block font-mono text-sm">
                {flag.key}
              </Label>
              <p className="text-sm text-muted-foreground">
                {flag.description ?? t(`admin.flags.keys.${flag.key}`, { defaultValue: '' })}
              </p>
              {flag.updatedAt && (
                <p className="text-xs text-muted-foreground">
                  {t('common.updated')}: {formatDate(flag.updatedAt, i18n.language, true)}
                </p>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {flag.enabled ? t('admin.flags.on') : t('admin.flags.off')}
            </span>
            <Switch
              id={`flag-${flag.key}`}
              checked={flag.enabled}
              onCheckedChange={(enabled) => update.mutate({ key: flag.key, enabled })}
              data-testid={`flag-${flag.key}`}
            />
          </li>
        ))}
      </ul>
    </AdminPage>
  );
}
